import { trim, deepEqual, deepCopy, getFocusable, hide, show } from "../utils";
import { IAnalyticsService, IMessagingService } from "../services";
import { Container } from "./container";
import { LoadingEventType } from "../enums/loadingEventType";
import { BehaviorSubject } from "rxjs";
import { FilterInteractionsEvent, PaginationInteractionEvent, PageSizeInteractionEvent } from "../models/analytics";
import { CardsGridClickableComponent } from "./cardsGridClickableComponent";
import { AnalyticsEventName } from "../enums/analyticsEventName";

class SearchParams {
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    filters?: Array<any> = [];
}

class CardsContainer extends Container {
    private searchTermEl: HTMLInputElement;
    private cardListEl: HTMLUListElement;
    private filterEls: HTMLSelectElement[];

    private paginationEl: HTMLElement;
    private searchParams$: BehaviorSubject<SearchParams>;
    private areFiltersEnabled: boolean;
    private searchFiltersContainerEl: HTMLElement;
    private searchBtnEl: HTMLElement;
    private advancedToggleEl: HTMLInputElement;
    private advancedToggleLabelEl: HTMLElement;
    private mobileSearchBtnOpenEl: HTMLElement;
    private mobileSearchBtnCloseEl: HTMLElement;
    private focusToElOnOpen: HTMLElement;
    private firstFocusableEl: HTMLElement;
    private lastFocusableEl: HTMLElement;

    private noResultsFoundEl: HTMLElement;

    private previousSearchParams: SearchParams = new SearchParams();
    private filterValueSeparator;
    readonly searchEventFilterCategory = "search";

    constructor(
        containerEl: HTMLElement,
        protected messagingService: IMessagingService,
        protected analyticsService: IAnalyticsService) {
        super(containerEl, messagingService);

        // initialise our common elements
        this.cardListEl = containerEl.querySelector<HTMLUListElement>(".js-cards-list");
        this.paginationEl = containerEl.querySelector<HTMLElement>(".js-pagination");
        this.areFiltersEnabled = containerEl.dataset.areFiltersEnabled?.toLowerCase() === "true";

        this.noResultsFoundEl = containerEl.querySelector<HTMLElement>(".js-no-results-found");

        // initialise our filter controls
        this.searchTermEl = containerEl.querySelector<HTMLInputElement>(".js-cards-searchTermInput");
        this.filterEls = [...containerEl.querySelectorAll<HTMLSelectElement>(".js-cards-filter")];
        const advancedFilterFormEl = containerEl.querySelector<HTMLFormElement>(".js-cards-advanced-filters-form");

        // setup default search params based on any supplied input from the querystring
        const searchParams = this.getSearchParamsFromUrl();
        this.searchParams$ = new BehaviorSubject<SearchParams>(searchParams);

        if (!deepEqual(searchParams, new SearchParams())) {
            history.pushState(searchParams, window.location.href);
        }

        if (this.areFiltersEnabled) {
            
            this.searchFiltersContainerEl = containerEl.querySelector<HTMLElement>(".js-cards-filters-container");
            this.filterValueSeparator = this.searchFiltersContainerEl.dataset.filterValueSeparator;

            // add event listeners for our filter controls
            this.searchBtnEl = containerEl.querySelector<HTMLButtonElement>(".js-cards-searchButton");
            this.searchBtnEl.addEventListener("click", e => this.onSearchBtnClicked(e));

            const resetBtnEls = [...containerEl.querySelectorAll<HTMLButtonElement>(".js-cards-resetButton")];
            resetBtnEls.forEach((el) => el.addEventListener("click", (e) => this.onResetBtnClicked(e)));

            const filterFormEl = containerEl.querySelector<HTMLFormElement>(".js-cards-filtersForm");
            filterFormEl.addEventListener("change", e => this.onFilterChanged(e));

            if (advancedFilterFormEl) {
                advancedFilterFormEl.addEventListener("change", e => this.onFilterChanged(e));

                // Advanced view toggle
                this.advancedToggleEl = containerEl.querySelector<HTMLInputElement>(".js-cards-toggleAdvanced");
                this.advancedToggleEl.addEventListener("change", e => this.onAdvancedToggleToggled(e));
                this.advancedToggleLabelEl = containerEl.querySelector<HTMLElement>(".js-cards-toggleAdvanced-label");
                
                this.advancedToggleEl.addEventListener("keydown", (e: KeyboardEvent) => {
                    switch(e.key) {
                        case "Enter":
                            this.advancedToggleEl.checked = !this.advancedToggleEl.checked;
                            this.advancedToggleEl.dispatchEvent(new Event("change", {
                                bubbles: true,
                                cancelable: true
                            }));
                            break;
                    }
                });
            }
            // Mobile view toggles
            this.mobileSearchBtnOpenEl = containerEl.querySelector<HTMLElement>(".js-cards-filters-open");
            this.mobileSearchBtnOpenEl.addEventListener("click", e => this.onMobileSearchBtnOpenClicked(e));
            this.mobileSearchBtnCloseEl = containerEl.querySelector<HTMLElement>(".js-cards-filters-close");
            this.mobileSearchBtnCloseEl.addEventListener("click", e => this.onMobileSearchBtnCloseClicked(e));
            const mobileSearchBtnApplyEl = containerEl.querySelector<HTMLElement>(".js-cards-filters-apply");
            mobileSearchBtnApplyEl.addEventListener("click", e => this.onMobileSearchBtnApplyClicked(e));
            this.mobileSearchTogglePanelFocus = this.mobileSearchTogglePanelFocus.bind(this);

            // Mobile view - Focusable elements
            const focusableEls = getFocusable(this.searchFiltersContainerEl) as HTMLElement[];
            this.firstFocusableEl = [...focusableEls][0];
            this.lastFocusableEl = [...focusableEls].pop();
            this.focusToElOnOpen = focusableEls.find(el => el.tagName.toLowerCase() === 'input');
        }

        // listen for any changes to our filters params. This will trigger a fresh search.
        // skip the first one, it'll always be from the initial page load
        this.searchParams$.pipe().subscribe(searchParams => this.onSearchParamsChanged(searchParams));

        window.addEventListener('popstate', (e => this.onPopState(e)));
        this.addPagingEventListners();
    }

    private onSearchBtnClicked(e) {
        e.preventDefault();
        const searchParams = this.searchParams$.getValue();
        searchParams.page = null;
        this.searchTermEl.value = this.searchTermEl.value.trim().replace(/\s+/g, ' ');
        searchParams.searchTerm = this.searchTermEl.value;
        this.searchParams$.next(searchParams);
        if (this.searchFiltersContainerEl.getAttribute("aria-hidden") === "false") {
            this.mobileSearchBtnOpenEl.focus();
        }
        this.mobileSearchTogglePanel(false);

        // record search event
        if (searchParams.searchTerm) {
            this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(this.searchEventFilterCategory, searchParams.searchTerm));
        }
    }

    private onFilterChanged(e) {
        e.preventDefault();
        if (e.target === this.searchTermEl || e.target === this.advancedToggleEl) return; // Search/Advanced toggle input changes handled elsewhere  
        const searchParams = this.searchParams$.getValue();
        searchParams.page = null;
        searchParams.filters = [];

        this.filterEls.forEach(f => {
            const valuesAsString = Array.from(f.selectedOptions).map(option => option.value).join(this.filterValueSeparator);
            if (valuesAsString !== "") {
                searchParams.filters.push({ name: f.name, value: valuesAsString});
           }
        });

        // record filter interaction from user to GTM
        // for standard filters
        if (e.target.type == 'select-one' && e.target.name.length > 0 && e.target.value.length > 0)
        {
            this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(e.target.name.toLowerCase(), e.target.value.toLowerCase()));
        }

        // and now for multi select checked items
        if (e.target.type == 'checkbox' && e.target.value.length > 0)
        {
            const dropdownWrapper = e.target.closest('.js-form-field-dropdown-wrapper');
            const parentSelect = dropdownWrapper?.querySelector('select.js-cards-filter');

            if (e.target.checked && parentSelect?.name) {
                this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(parentSelect.name.toLowerCase(), e.target.value.toLowerCase()));
            }
        }

        this.searchParams$.next(searchParams);
    }

    private onResetBtnClicked(e) {
        e.preventDefault();
        this.searchParams$.next(new SearchParams());
        this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteractionClear);
    }

    private onPageSizeBtnClicked(e: PointerEvent) {
        e.preventDefault();
        const btn = e.target as HTMLElement;
        const searchParams = this.searchParams$.getValue();
        searchParams.page = null;
        searchParams.pageSize = parseInt(btn.dataset.pageSize);
        this.analyticsService.RecordEvent(AnalyticsEventName.PageSize, new PageSizeInteractionEvent(btn.dataset.pageSize));
        this.searchParams$.next(searchParams);
    }

    private onPageBtnClicked(e: PointerEvent) {

        e.preventDefault();
        const btn = e.target as HTMLElement;
        const searchParams = this.searchParams$.getValue();
        searchParams.page = parseInt(btn.dataset.page);
        this.analyticsService.RecordEvent(AnalyticsEventName.Pagination, new PaginationInteractionEvent(btn.dataset.page));
        this.searchParams$.next(searchParams);
    }

    private onPopState(e: PopStateEvent) {
        // triggered when the user navigates 'back'. just
        // reset the search params to the previous state
        if (e.state != null) { this.searchParams$.next(e.state) };
    }

    private async onSearchParamsChanged(searchParams: SearchParams) {
        // When our SearchParams change we'll do three things:
        // - update the page url
        // - update the filters UI
        // - carry out an API call and update the results list
        if (deepEqual(searchParams, this.previousSearchParams)) {
            return;
        }

        this.previousSearchParams = deepCopy(searchParams);

        // build up our filter url
        let filterUrl = "";

        if (searchParams.page) {
            filterUrl += `page=${searchParams.page}&`;
        }

        if (searchParams.pageSize) {
            filterUrl += `pageSize=${searchParams.pageSize}&`;
        }

        if (searchParams.searchTerm) {
            filterUrl += `searchTerm=${searchParams.searchTerm}&`;
        }

        searchParams.filters.forEach(f => {
            filterUrl += `${f.name}=${encodeURIComponent(f.value)}&`;

            // find the field that the filter relates to. We'll swap out the filter name for it's more readable label
            let eventName = f.name;
            const filterFields = document.getElementsByName(eventName);

            if (filterFields && filterFields.length > 0) {

                // find the label that describes the filter field
                const filterLabel = document.querySelector<HTMLLabelElement>(`label[for="${filterFields[0].id}"]`);
                if (filterLabel !== null) {
                    eventName = filterLabel.innerText;
                }
            }
        });

        filterUrl = trim(filterUrl, '&').toLowerCase();

        var pageUrl = trim(`${window.location.origin}${window.location.pathname}?${filterUrl}`, "?");

        if (this.areFiltersEnabled) {
            // update UI elements
            this.searchTermEl.value = searchParams.searchTerm ?? "";
            this.filterEls.forEach(filterEl => {
                const filter = searchParams.filters.find(searchParam => searchParam.name === filterEl.name);
                if (filter) {
                    const selectedValues = filter.value.split(this.filterValueSeparator);
                    if (filterEl.multiple) {
                        Array.from(filterEl.options).forEach(option => {
                            option.selected = selectedValues.includes(option.value);
                        });
                        const changeEvent = new Event("change");
                        filterEl.dispatchEvent(changeEvent);
                    } else {
                        filterEl.value = selectedValues[0] ?? "";
                    }
                } else {
                    // No matching filter found; deselect all options
                    if (filterEl.multiple) {
                        Array.from(filterEl.options).forEach(option => {
                            option.selected = false;
                        });
                        const changeEvent = new Event("change");
                        filterEl.dispatchEvent(changeEvent);
                    } else {
                        filterEl.value = "";
                    }
                }
            });
        }

        // if the filters url already matches the page url then no need to carry out a search
        if (pageUrl == window.location.href) {
            return;
        }

        // update browser url and push change into history stack
        history.pushState(searchParams, "", pageUrl);

        // showing the loading indicator whilst the search is being carried out
        this.messagingService.StartLoadingEvent(this.containerId, LoadingEventType.Page);

        // call the api to find matching cards
        var apiUrl = `/containers/${this.containerId}/filter?${filterUrl}`;
        const apiResponse = await fetch(apiUrl, { method: "GET" });
        if (apiResponse.ok) {
            // the response from API is a partial html view with the updated results
            var responseContent = await apiResponse.text();
            const parser = new DOMParser();

            // parse the html and replace the relevant elements
            const html = parser.parseFromString(responseContent, "text/html");
            const updatedCardListEl = html.querySelector<HTMLUListElement>(".js-cards-list");
            const updatedPaginationEl = html.querySelector<HTMLElement>(".js-pagination");

            if (this.noResultsFoundEl != null) {
                this.noResultsFoundEl.textContent = "";

                if (updatedCardListEl.firstElementChild.innerHTML.includes("no results matching your criteria")) {
                    this.noResultsFoundEl.textContent = "There are zero results matching your criteria.";
                }
                else {
                    this.noResultsFoundEl.textContent = updatedCardListEl.childElementCount === 1 ? "There is 1 result matching your criteria." : `There are ${updatedCardListEl.childElementCount} results matching your criteria.`;
                }
            }

            // replace the card list and pagination elements with the updated response elements
            if (this.cardListEl && updatedCardListEl)
            {
                this.cardListEl.innerHTML = updatedCardListEl.innerHTML;
            }
            if (this.paginationEl && updatedPaginationEl) {
                this.paginationEl.innerHTML = updatedPaginationEl.innerHTML;
            } 
            else if (this.paginationEl && !updatedPaginationEl) {
                // Clear inner HTML since the content endpoint returned no content for pagination
                // controls meaning that the content result is smaller than the page size.
                this.paginationEl.innerHTML = "";
            }

            // our paging buttons have been replaced so rebind the event listners
            this.addPagingEventListners();
            this.addClickableCardListners();
            this.containerEl.scrollIntoView();
            this.messagingService.EndLoadingEvent(this.containerId);
        }
        else {
            // show notificatino
            this.messagingService.EndLoadingEvent(this.containerId);
            console.log(apiResponse.statusText);
            const error = new Error("Card fetch failed");
            return Promise.reject(error);
        }
    }

    private getSearchParamsFromUrl(): SearchParams {
        const queryString = new URLSearchParams(window.location.search.toLowerCase());
        const searchParams = new SearchParams();
        const page = queryString.get("page");

        if (page) {
            searchParams.page = parseInt(page);
        }

        const pageSize = queryString.get("pagesize");

        if (pageSize) {
            searchParams.pageSize = parseInt(pageSize);
        }

        if (this.areFiltersEnabled) {
            searchParams.filters = [];
            this.filterEls.forEach(f => {
                const filterValue = queryString.get(f.name);

                if (filterValue) {
                    searchParams.filters.push({ name: f.name, value: filterValue })
                    // for each filter in querystring params record filter event into GTM
                    if (filterValue.includes("|"))
                    {
                        const selectedValues = filterValue.split("|");
                        selectedValues.forEach(element => {
                            this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(f.name.toLowerCase(), element.toLowerCase()));
                        });
                    }
                    else
                    {
                        this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(f.name.toLowerCase(), filterValue.toLowerCase()));
                    }
                 }
            });

            const searchTerm = queryString.get("searchterm");

            if (searchTerm) {
                searchParams.searchTerm = searchTerm;
                this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent(this.searchEventFilterCategory, searchParams.searchTerm));
            }
        }
        return searchParams;
    }

    private addPagingEventListners() {
        if (this.paginationEl) {
            this.paginationEl.querySelectorAll<HTMLElement>(".js-pagination-pageButton")
                .forEach(p => p.addEventListener("click", (e: PointerEvent) => this.onPageBtnClicked(e)));

            this.paginationEl.querySelectorAll<HTMLElement>(".js-pagination-pageSizeButton")
                .forEach(p => p.addEventListener("click", (e: PointerEvent) => this.onPageSizeBtnClicked(e)));
        }

    }

    private addClickableCardListners() {

        if (this.cardListEl) {
            this.cardListEl.querySelectorAll(".js-card-clickable[data-url]").forEach((element: HTMLElement) => {
                new CardsGridClickableComponent(element, this.messagingService);
            });
        }
    }


    /* Advanced toggle methods */
    private onAdvancedToggleToggled(e) {
        const checkbox = e.target as HTMLInputElement;
        if (this.advancedToggleLabelEl) {
            this.analyticsService.RecordEvent(AnalyticsEventName.CardsFilterInteraction, new FilterInteractionsEvent("advanced filter", this.advancedToggleLabelEl.innerText.toLowerCase()));
            this.advancedToggleLabelEl.innerText = (checkbox.checked == true ? 'Hide' : 'Show');
        }
    }

    /* Mobile specific user input methods */
    private onMobileSearchBtnOpenClicked(e) {
        e.preventDefault();
        this.mobileSearchTogglePanel(true);
        this.focusToElOnOpen.focus();
        this.searchFiltersContainerEl.addEventListener("keydown", this.mobileSearchTogglePanelFocus);
    }

    private onMobileSearchBtnCloseClicked(e) {
        e.preventDefault();
        this.mobileSearchTogglePanel(false);
        this.mobileSearchBtnOpenEl.focus();
        this.searchFiltersContainerEl.removeEventListener("keydown", this.mobileSearchTogglePanelFocus);
    }

    private onMobileSearchBtnApplyClicked(e) {
        e.preventDefault();
        this.searchBtnEl.click();
        this.mobileSearchBtnCloseEl.click();
    }

    private mobileSearchTogglePanel(state) {
        this.searchFiltersContainerEl.setAttribute("aria-hidden", `${!state}`);
        this.mobileSearchBtnOpenEl.setAttribute("aria-expanded", `${state}`);
        state ? document.body.classList.add('js-with-side-nav-open') : document.body.classList.remove('js-with-side-nav-open');
    }

    private mobileSearchTogglePanelFocus = (e: KeyboardEvent) => {
        const shiftKeyPressed = e.shiftKey;
        switch(e.key) {
            case "Escape":
                this.mobileSearchBtnCloseEl.click();
                break;
            case "Tab":
                if (e.target === this.lastFocusableEl && !shiftKeyPressed) {
                    e.preventDefault();
                    this.firstFocusableEl.focus();
                }
                break;
        }
    }


}

export { CardsContainer };
